import { has, includes, snakeCase, sum, toUpper, uniq } from 'lodash';
import { createAction } from 'redux-actions';
import { callApi, getEndpoint } from '../../../utils/api';
import { formatUnixDate } from '../../../utils/dates/date-utility';
import { generateExistingPurchase } from '../../../utils/submission/submission-utility';
import { analyticsDataLayer } from '../../../redux/utils/analytics-helper';

const EXECUTE_COLLEGE_CONTRIBUTE = 'EXECUTE_COLLEGE_CONTRIBUTE';

/**
 *
 * @param transactions - this is returned in the body of the execute purchase POST
 * @returns {String} this will be an empty string in the absence of a confirm number
 */
export const getConfirmationNumber = (transactions) => {
  return Array.isArray(transactions) &&
    transactions.length > 0 &&
    transactions[0].confirmationNumber
    ? transactions[0].confirmationNumber
    : '';
};

/**
 *
 * @param transactions - this is returned in the body of the execute purchase POST
 * @returns {String} this will be an empty string in the absence of a trade date, if event of trade date this
 * will return a formated string in the shape of 01/01/2018
 */
export const getTradeDate = (transactions) => {
  return Array.isArray(transactions) &&
    transactions.length > 0 &&
    transactions[0].tradeDate
    ? formatUnixDate(transactions[0].tradeDate)
    : '';
};

const hasTimedOut = (payload) => {
  try {
    return (
      has(payload, 'text') &&
      includes(payload.text, '{') &&
      JSON.parse(payload.text).error === 'timeout'
    );
  } catch (e) {
    return false;
  }
};

const printErrorMessage = (payloadBody) => {
  return has(payloadBody, 'message') ? payloadBody.message : '';
};

export default function executeCollegeContribute(
  state = {
    isLoaded: false,
    isLoading: false,
    hasFailed: false,
    error: null,
    transactions: [],
    confirmationNumber: '',
    tradeDate: '',
    paperlessUpdate: null,
  },
  { payload, type }
) {
  switch (type) {
    case `${EXECUTE_COLLEGE_CONTRIBUTE}_PENDING`:
      return {
        ...state,
        isLoaded: false,
        isLoading: true,
        transactions: [],
      };
    case `${EXECUTE_COLLEGE_CONTRIBUTE}_FULFILLED`:
      return {
        ...state,
        isLoaded: true,
        isLoading: false,
        hasFailed: false,
        transactions: payload.body.transactions,
        confirmationNumber: getConfirmationNumber(payload.body.transactions),
        tradeDate: getTradeDate(payload.body.transactions),
        paperlessUpdate: payload.body.paperlessUpdate,
      };
    case `${EXECUTE_COLLEGE_CONTRIBUTE}_REJECTED`:
      return {
        ...state,
        isLoaded: true,
        isLoading: false,
        hasFailed: true,
        timeout: hasTimedOut(payload),
        message: printErrorMessage(payload.body),
        error: payload.body,
      };
    default:
      return state;
  }
}

export const submitCollegeContribute = (
  availableFunds,
  paperless,
  priorPaperless,
  purchasingFunds,
  selectedBank
) => {
  const payload = generateExistingPurchase(
    availableFunds,
    paperless,
    priorPaperless,
    purchasingFunds,
    selectedBank
  );

  try {
    const getAnalyticsDataForTransaction = (payload) => {
      const {
        transactionElement: { transactionTargets },
      } = payload;
      const purchaseAmounts = transactionTargets.map((target) => {
        const { contributionYearData } = target;
        return sum(contributionYearData.map((cd) => cd.amount));
      });

      return {
        amount: sum(purchaseAmounts),
      };
    };

    const getAccountTypesFromSubmission = (purchasingFunds) => {
      return {
        accountTypes: uniq(
          purchasingFunds.map((fund) => toUpper(snakeCase(fund.productType)))
        ),
      };
    };

    const submissionDataLayer = {
      quantity: purchasingFunds.length,
      ...getAccountTypesFromSubmission(purchasingFunds),
      ...getAnalyticsDataForTransaction(payload),
    };
    analyticsDataLayer(submissionDataLayer);
  } catch (e) {
    // don't let analytics errors cause other things to fail
  }

  return (dispatch) => {
    const action = createAction(EXECUTE_COLLEGE_CONTRIBUTE)(
      callApi(getEndpoint('finalPurchase'), { method: 'POST' }, payload)
    );
    dispatch(action);
  };
};
